import React, { useEffect, useMemo, useState } from 'react';
import { Plus, ClipboardCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import useOrganizationMemberActivityStore from '@/stores/AdminStore/OrganizationMemberActivityStore';
import { LoadingSpinner } from '@/Components/common';
import AssignAssessmentModal from '../modals/AssignAssessmentModal';

interface Props { userId: string; }

const formatDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

const AssessmentsTab: React.FC<Props> = ({ userId }) => {
  const {
    assessments, loadingAssessments, assigning,
    fetchAssessments, fetchAssignable, assignAssessment
  } = useOrganizationMemberActivityStore();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => { fetchAssessments(userId); }, [userId, fetchAssessments]);

  const { pending, submissions } = useMemo(() => ({
    pending: assessments.filter((a) => a.kind === 'pending'),
    submissions: assessments.filter((a) => a.kind === 'submission'),
  }), [assessments]);

  const handleAssign = async (formId: number, dueDate: string | null) => {
    const res = await assignAssessment(userId, formId, dueDate);
    if (res.success) {
      toast.success(res.message);
      setShowModal(false);
    } else {
      toast.error(res.message);
    }
  };

  if (loadingAssessments) {
    return <div style={{ textAlign: 'center', padding: 32 }}><LoadingSpinner size="md" /></div>;
  }

  return (
    <div>
      <div className="omact-toolbar">
        <h3 style={{ margin: 0, fontSize: 16 }}>Assessments</h3>
        <button className="omact-btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={16} /> Assign assessment
        </button>
      </div>

      <h4 style={{ fontSize: 14, color: '#374151', marginBottom: 8 }}>Pending ({pending.length})</h4>
      {pending.length === 0 ? (
        <p style={{ color: '#6b7280', fontSize: 14 }}>No pending assessments.</p>
      ) : (
        <table className="omact-table" style={{ marginBottom: 24 }}>
          <thead>
            <tr>
              <th>Title</th>
              <th>Status</th>
              <th>Assigned</th>
              <th>Due</th>
            </tr>
          </thead>
          <tbody>
            {pending.map((a) => (
              <tr key={a.instance_id ?? a.form_id}>
                <td>{a.title}</td>
                <td><span className={`omact-badge omact-badge-${a.status}`}>{a.status.replace('_', ' ')}</span></td>
                <td>{formatDate(a.assigned_at)}</td>
                <td>{formatDate(a.due_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h4 style={{ fontSize: 14, color: '#374151', marginBottom: 8 }}>Submissions ({submissions.length})</h4>
      {submissions.length === 0 ? (
        <div className="omact-empty">
          <ClipboardCheck size={40} color="#9ca3af" />
          <p>No submissions yet.</p>
        </div>
      ) : (
        <table className="omact-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Submitted</th>
              <th>Score</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            {submissions.map((a) => (
              <tr key={`${a.form_id}-${a.submitted_at}`}>
                <td>{a.title}</td>
                <td>{formatDate(a.submitted_at)}</td>
                <td>{a.score != null ? `${a.score}${a.max_score != null ? ` / ${a.max_score}` : ''}` : '—'}</td>
                <td>{a.percentage != null ? `${Math.round(Number(a.percentage))}%` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <AssignAssessmentModal
          userId={userId}
          assigning={assigning}
          fetchAssignable={fetchAssignable}
          onConfirm={handleAssign}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default AssessmentsTab;
